import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

/**
 * Client-portal auth store (epic 128) — deliberately SEPARATE from the staff
 * `useAuthStore`.
 *
 * The external client persona (CLIENT_VIEWER) is a different principal with a
 * different login (`POST /api/v1/portal/login`) and a different, narrower token
 * shape: a single 8h access token with NO refresh token and NO role union. It
 * must never share state with a staff session — a logged-in operator and a
 * logged-in client can coexist in the same browser without clobbering each
 * other, and the portal token must never leak onto staff `/api/v1/*` calls (or
 * vice-versa). Hence its own store key and its own axios client
 * (`lib/portalClient.ts`).
 */
export interface PortalSession {
  accessToken: string;
  accessTokenExpiresAt: string;
  tenantId: string;
  /** The single account this client is scoped to (the JWT `acct` claim). */
  accountId: string;
}

interface PortalAuthState {
  session: PortalSession | null;
  setSession: (session: PortalSession) => void;
  clear: () => void;
  isAuthenticated: () => boolean;
}

export const usePortalAuthStore = create<PortalAuthState>()(
  persist(
    (set, get) => ({
      session: null,
      setSession: (session) => set({ session }),
      clear: () => set({ session: null }),
      isAuthenticated: () => {
        const s = get().session;
        if (!s) return false;
        // Treat an expired access token as unauthenticated: there is no refresh
        // path for portal sessions, so the guard must bounce to re-login rather
        // than fire doomed 401s.
        return new Date(s.accessTokenExpiresAt).getTime() > Date.now();
      },
    }),
    {
      name: 'kairos.portal.auth',
      storage: createJSONStorage(() => localStorage),
    },
  ),
);
